/**
 * ============================================================================
 * WEATHER CHAT PAGE (Skye Chatbot)
 * ============================================================================
 * AI-powered weather assistant for NSAS pilots
 * 
 * SECTIONS:
 * ---------
 * [1] IMPORTS & CONSTANTS     - Lines 1-30
 * [2] STATE & REFS            - Lines 30-50
 * [3] EFFECTS                 - Lines 50-145
 *     - SkyLegs warning toast
 *     - PWA install prompt
 *     - Auto-scroll messages
 *     - Welcome message generation
 * [4] HANDLERS                - Lines 145-400
 *     - handleLogin, handleLogout
 *     - handleSubmit (chat)
 *     - handleSaveDraft
 *     - formatContent
 * [5] LOGIN SCREEN            - Lines 400-500
 * [6] MAIN CHAT INTERFACE     - Lines 500-800
 *     - Header with avatar
 *     - Message list
 *     - Input area with quick buttons
 *     - Skye Profile Modal
 * 
 * EXTRACTED COMPONENTS:
 * - /components/chat/SkyeProfileModal.jsx
 * ============================================================================
 */

import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { 
  MessageCircle, Send, Loader2, Plane, Sparkles, Save, Trash2, 
  LogOut, User, Cloud, Wind, Thermometer, Eye, AlertTriangle,
  CheckCircle, XCircle, Clock, MapPin, FileText, Download, Smartphone, Lock, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';
import SkyeProfileModal from '../components/chat/SkyeProfileModal';

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// [1] CONSTANTS
// =============================================================================

// Valid pilot trigrams from SkyLegs with their PINs
const PILOT_CREDENTIALS = {
  'FDT': '1960',
  'MVE': '1960', 
  'NMA': '1960',
  'PVG': '1960',
  'SDW': '1960',
  'SKI': '1960',
  'SME': '1960',
  'TGE': '1960',
  'JVH': '1960',
  'XDU': '1960'
};
const VALID_TRIGRAMS = Object.keys(PILOT_CREDENTIALS);

// =============================================================================
// [2] MAIN COMPONENT - STATE & REFS
// =============================================================================

const WeatherChatPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pilotTrigram, setPilotTrigram] = useState('');
  const [trigramInput, setTrigramInput] = useState('');
  const [pinInput, setPinInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [draftCount, setDraftCount] = useState(0);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [pilotFlights, setPilotFlights] = useState(null);
  const [allFlights, setAllFlights] = useState([]);
  const [showSkyeProfile, setShowSkyeProfile] = useState(false);
  const [skyLegsStatus, setSkyLegsStatus] = useState({ available: true, message: null });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ===========================================================================
  // [3] EFFECTS
  // ===========================================================================

  // SkyLegs status is now just logged, not shown as toast (to avoid confusing users)

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    
    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/nsas-ai-sw.js')
        .then((registration) => {
          console.log('NSAS.AI SW registered:', registration.scope);
        })
        .catch((error) => {
          console.log('NSAS.AI SW registration failed:', error);
        });
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
    }
    setInstallPrompt(null);
    setShowInstallBanner(false);
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when authenticated and flights loaded
  useEffect(() => {
    if (isAuthenticated && pilotFlights !== null) {
      inputRef.current?.focus();
      
      // Current Zulu time
      const nowZ = new Date().toISOString().slice(0, 16).replace('T', ' ') + 'Z';
      
      // Build welcome message with pilot's flights
      let flightInfo = '';
      if (pilotFlights.length > 0) {
        // Filter to only future/today flights
        const today = new Date().toISOString().slice(0, 10);
        const upcomingFlights = pilotFlights.filter(f => f.flight_date >= today);
        
        if (upcomingFlights.length > 0) {
          flightInfo = '\n\n**Your flights:**\n';
          upcomingFlights.slice(0, 4).forEach(f => {
            const isToday = f.flight_date === today;
            const status = f.status === 'EXECUTED' ? '✓' : isToday ? '🔴' : '📅';
            flightInfo += `${status} **${f.flight_number || 'Flight'}** ${f.departure_airport}→${f.arrival_airport || f.departure_airport} | ${f.flight_date?.slice(5)} ${f.departure_time || ''}Z | ${f.aircraft_reg}\n`;
          });
        } else {
          flightInfo = '\n\nNo upcoming flights in schedule.';
        }
      } else {
        flightInfo = '\n\n⚠️ **SkyLegs API unavailable** - flight schedule data may be rate-limited\n\nNo flights found in your schedule.';
      }
      
      // Get unique airports from pilot's flights for quick buttons
      const flightAirports = [...new Set(pilotFlights.flatMap(f => [f.departure_airport, f.arrival_airport]).filter(Boolean))];
      
      setMessages([{
        role: 'assistant',
        content: `Hi **${pilotTrigram}**, what could I do to please you today? 💋

${nowZ}${flightInfo}

Just ask me anything - weather, schedule, crew, or whatever's on your mind!`,
        timestamp: new Date().toISOString(),
        flightAirports: flightAirports
      }]);
    }
  }, [isAuthenticated, pilotTrigram, pilotFlights]);

  // ===========================================================================
  // [4] HANDLERS
  // ===========================================================================

  const handleLogin = async () => {
    const trigram = trigramInput.toUpperCase().trim();
    const pin = pinInput.trim();
    
    if (!VALID_TRIGRAMS.includes(trigram)) {
      toast.error('Invalid trigram. Please use your SkyLegs pilot code.');
      return;
    }
    
    if (PILOT_CREDENTIALS[trigram] !== pin) {
      toast.error('Invalid PIN. Please try again.');
      return;
    }
    
    setPilotTrigram(trigram);
    toast.success(`Welcome ${trigram}!`);
    
    // Fetch flights for this pilot BEFORE setting authenticated
    try {
      const response = await axios.get(`${API}/api/ops/skylegs/flights?days=14&include_meta=true`);
      console.log('SkyLegs response:', JSON.stringify(response.data));
      if (response.data) {
        // Handle both formats: with metadata or just array
        const flightsData = response.data.flights || response.data;
        
        // Only show warning if we have NO data at all
        // If flights are showing (even from cache), don't confuse the user
        if (Array.isArray(flightsData) && flightsData.length === 0) {
          console.log('Setting SkyLegs warning - no flights available');
          setSkyLegsStatus({ 
            available: false, 
            message: 'No flights found in your schedule'
          });
        }
        
        setAllFlights(Array.isArray(flightsData) ? flightsData : []);
        // Filter flights where this pilot is crew (handle emojis in names)
        const myFlights = (Array.isArray(flightsData) ? flightsData : []).filter(f => 
          f.crew && f.crew.some(c => {
            const crewName = String(c).toUpperCase();
            return crewName.includes(trigram);
          })
        );
        console.log(`Found ${myFlights.length} flights for ${trigram}:`, myFlights);
        setPilotFlights(myFlights);
      } else {
        setPilotFlights([]);
      }
    } catch (error) {
      console.error('Failed to fetch flights:', error);
      setSkyLegsStatus({ available: false, message: 'Connection failed' });
      setPilotFlights([]);
    }
    
    // Set authenticated AFTER flights are loaded
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPilotTrigram('');
    setPilotFlights(null);
    setMessages([]);
    setSessionId(null);
    setDraftCount(0);
    toast.success('Logged out');
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    setIsLoading(true);

    try {
      // Extract ICAO codes from user message (4-letter codes starting with common prefixes)
      const icaoPattern = /\b([A-Z]{4})\b/g;
      const potentialIcaos = userMessage.toUpperCase().match(icaoPattern) || [];
      // Filter to likely airport codes (start with common prefixes)
      const validPrefixes = ['LE', 'LF', 'EB', 'EG', 'EH', 'ED', 'LI', 'LP', 'GC', 'K', 'EI', 'LO', 'LS', 'LZ'];
      const icaoCodes = potentialIcaos.filter(code => 
        validPrefixes.some(prefix => code.startsWith(prefix)) || 
        ['EBOS', 'LFAC', 'LFAT', 'LEMG', 'LEAM', 'LEAL', 'KJFK', 'EGLL'].includes(code)
      );
      
      // Get airports from pilot's flights if no ICAO in message
      const flightAirports = [...new Set(pilotFlights.flatMap(f => [f.departure_airport, f.arrival_airport]).filter(Boolean))];
      const defaultAirports = flightAirports.length > 0 ? flightAirports : ['EBOS', 'LEMG'];
      
      const requestBody = {
        message: userMessage,
        pilot_trigram: pilotTrigram,
        icao_codes: icaoCodes.length > 0 ? icaoCodes : defaultAirports,
        pilot_flights: pilotFlights.slice(0, 5)  // Send pilot's flights for context
      };
      
      if (sessionId) requestBody.session_id = sessionId;

      const response = await axios.post(`${API}/api/chat/weather`, requestBody);

      if (response.data) {
        if (!sessionId) {
          setSessionId(response.data.session_id);
        }

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.response,
          timestamp: response.data.timestamp,
          sources: response.data.sources_used
        }]);

        // Check if user mentioned saving/draft
        if (response.data.suggest_draft) {
          setTimeout(() => {
            setMessages(prev => [...prev, {
              role: 'system',
              content: '💾 Would you like me to save this as a draft report? You can access it later in the NSAS TOOLBOX library.',
              isDraftPrompt: true,
              timestamp: new Date().toISOString()
            }]);
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '⚠️ Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const createDraftReport = async () => {
    if (!sessionId) {
      toast.error('No active chat session');
      return;
    }

    setIsLoading(true);
    try {
      // Get airports from pilot's flights
      const airports = [...new Set(pilotFlights?.flatMap(f => [f.departure_airport, f.arrival_airport]).filter(Boolean) || [])];
      
      // Get first upcoming flight details
      const nextFlight = pilotFlights?.[0] || {};
      const ftxDesignator = nextFlight.flight_number || '';
      const aircraftReg = nextFlight.aircraft_reg || '';
      const aircraftType = nextFlight.aircraft_type || '';
      const crew = nextFlight.crew || [];
      const flightDate = nextFlight.flight_date || '';
      const departureTime = nextFlight.departure_time || '';
      const arrivalTime = nextFlight.arrival_time || '';
      
      // Build chat summary from recent messages
      const chatSummary = messages.filter(m => m.role !== 'system').slice(-10).map(m => 
        `${m.role === 'user' ? pilotTrigram : 'Skye'}: ${m.content.substring(0, 300)}`
      ).join('\n');

      const response = await axios.post(`${API}/api/chat/weather/draft-briefing`, {
        session_id: sessionId,
        pilot_trigram: pilotTrigram,
        airports: airports,
        ftx_designator: ftxDesignator,
        aircraft_reg: aircraftReg,
        aircraft_type: aircraftType,
        crew: crew,
        flight_date: flightDate,
        departure_time: departureTime,
        arrival_time: arrivalTime,
        chat_summary: chatSummary,
        weather_summary: messages.filter(m => m.role === 'assistant' && m.content.includes('METAR')).slice(-1)[0]?.content || '',
        pilot_flights: pilotFlights?.slice(0, 5) || []
      });

      if (response.data) {
        setDraftCount(prev => prev + 1);
        toast.success('Draft report saved!');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✅ **Draft report saved!**

Your briefing has been saved to the NSAS TOOLBOX library.

**Title:** ${response.data.title || 'NSAS.AI Draft'}
**FTX:** ${ftxDesignator || 'N/A'}
**Aircraft:** ${aircraftReg || 'N/A'}
**Airports:** ${response.data.airports?.join(', ') || 'N/A'}

To access it:
1. Go to NSAS TOOLBOX
2. Enter password: **diamond**
3. Click **Library** → Look for your draft

Draft ID: \`${response.data.draft_id.substring(0, 8)}...\``,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Draft creation error:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: `Chat cleared. How can I help you, ${pilotTrigram}?`,
      timestamp: new Date().toISOString()
    }]);
    setSessionId(null);
    toast.success('Chat cleared');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isAuthenticated) {
        sendMessage();
      } else {
        handleLogin();
      }
    }
  };

  // Format message content
  const formatContent = (content) => {
    if (!content) return '';
    let formatted = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/`([^`]+)`/g, '<code class="bg-slate-700 px-1 rounded text-sky-300">$1</code>');
    return formatted.split('\n').map((line, i) => (
      <span key={i}>
        <span dangerouslySetInnerHTML={{ __html: line }} />
        {i < content.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  // ===========================================================================
  // [5] LOGIN SCREEN
  // ===========================================================================

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 flex items-center justify-center p-4">
        <Helmet>
          <title>Skye - Your Personal Butler</title>
          <meta name="description" content="Your personal assistant for North Sea Aviation Services crew" />
          <meta name="theme-color" content="#0c4a6e" />
          <meta name="mobile-web-app-capable" content="yes" />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
          <meta name="apple-mobile-web-app-title" content="Skye" />
          <meta name="application-name" content="Skye" />
          <meta name="msapplication-TileColor" content="#0c4a6e" />
          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
          <link rel="manifest" href="/nsas-ai-manifest.json" />
          <link rel="apple-touch-icon" href="/nsas-logo.png" />
        </Helmet>
        <Toaster position="top-center" richColors />
        
        {/* Install Banner */}
        {showInstallBanner && (
          <div className="fixed top-0 left-0 right-0 bg-sky-600 text-white p-3 flex items-center justify-between z-50">
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              <span className="text-sm">Install Skye app for quick access</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={handleInstall}>
                Install
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowInstallBanner(false)}>
                ✕
              </Button>
            </div>
          </div>
        )}
        
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-full mb-4 px-8">
              <img src="/nsas-logo.png" alt="NSAS" className="w-full h-auto object-contain brightness-0 invert" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Skye</h1>
            <p className="text-sky-300">Your personal butler</p>
          </div>

          {/* Login Card */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8">
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Enter your NSAS Trigram
              </label>
              <Input
                type="text"
                placeholder="e.g., SDW"
                value={trigramInput}
                onChange={(e) => setTrigramInput(e.target.value.toUpperCase())}
                maxLength={3}
                className="text-center text-2xl font-mono tracking-widest uppercase bg-slate-900 border-slate-600 text-white h-14"
                autoFocus
              />
              <p className="text-xs text-slate-500 mt-2 text-center">
                Your 3-letter pilot identification code
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Enter your PIN
              </label>
              <Input
                type="password"
                placeholder="****"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                onKeyPress={handleKeyPress}
                maxLength={4}
                className="text-center text-2xl font-mono tracking-widest bg-slate-900 border-slate-600 text-white h-14"
              />
              <p className="text-xs text-slate-500 mt-2 text-center">
                Your 4-digit security PIN
              </p>
            </div>

            <Button
              onClick={handleLogin}
              disabled={trigramInput.length !== 3 || pinInput.length < 4}
              className="w-full h-12 bg-sky-600 hover:bg-sky-500 text-white font-medium"
            >
              <Lock className="w-5 h-5 mr-2" />
              Access Skye
            </Button>
          </div>

          {/* Footer */}
          <p className="text-center text-slate-500 text-sm mt-6">
            North Sea Aviation Services
          </p>
        </div>
      </div>
    );
  }

  // ===========================================================================
  // [6] MAIN CHAT INTERFACE
  // ===========================================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 flex flex-col">
      <Toaster position="top-center" richColors />
      
      {/* SkyLegs API Warning Banner - Show when all flights is empty */}
      {isAuthenticated && allFlights.length === 0 && (
        <div className="bg-amber-600/90 text-white px-4 py-2 text-center text-sm" data-testid="skylegs-warning">
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          SkyLegs API may be rate-limited - flight schedule unavailable
        </div>
      )}
      
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur border-b border-slate-700 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/skye.png" 
              alt="Skye" 
              className="w-10 h-10 rounded-full object-cover border-2 border-sky-400 cursor-pointer hover:border-sky-300 transition-all hover:scale-105" 
              onClick={() => setShowSkyeProfile(true)}
            />
            <div>
              <h1 className="text-lg font-bold text-white">Skye</h1>
              <p className="text-xs text-sky-300">Logged in as {pilotTrigram}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {draftCount > 0 && (
              <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                {draftCount} draft{draftCount > 1 ? 's' : ''} saved
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearChat}
              className="text-slate-400 hover:text-white"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto p-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-sky-600 text-white'
                    : msg.role === 'system'
                    ? 'bg-amber-900/50 border border-amber-700 text-amber-100'
                    : msg.isError
                    ? 'bg-red-900/50 border border-red-700 text-red-100'
                    : 'bg-slate-800/80 border border-slate-700 text-slate-100'
                }`}
              >
                {msg.role === 'assistant' && !msg.isError && (
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-600">
                    <img 
                      src="/skye.png" 
                      alt="Skye" 
                      className="w-8 h-8 rounded-full object-cover cursor-pointer hover:scale-105 transition-transform" 
                      onClick={() => setShowSkyeProfile(true)}
                    />
                    <span className="text-xs font-medium text-sky-400">Skye</span>
                  </div>
                )}
                
                <div className="text-sm leading-relaxed">
                  {formatContent(msg.content)}
                </div>

                {/* Draft prompt buttons */}
                {msg.isDraftPrompt && (
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      onClick={createDraftReport}
                      disabled={isLoading}
                      className="bg-amber-600 hover:bg-amber-500 text-white"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      Save Draft
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setMessages(prev => prev.filter((_, i) => i !== idx))}
                      className="text-slate-400"
                    >
                      Not now
                    </Button>
                  </div>
                )}

                {/* Timestamp */}
                {msg.timestamp && (
                  <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-slate-800/80 border border-slate-700 rounded-2xl px-4 py-3 flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-sky-400" />
                <span className="text-slate-300">Thinking...</span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input Area */}
      <footer className="bg-slate-900/80 backdrop-blur border-t border-slate-700 p-4 sticky bottom-0">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-3">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about weather conditions..."
              className="flex-1 px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
              rows={1}
              disabled={isLoading}
            />
            <Button
              onClick={sendMessage}
              disabled={isLoading || !inputValue.trim()}
              className="px-6 bg-sky-600 hover:bg-sky-500 rounded-xl"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          
          {/* Quick Actions - use airports from pilot's flights */}
          <div className="mt-3 flex flex-wrap gap-2">
            {/* Dynamic buttons from pilot's flights */}
            {(() => {
              const flightAirports = [...new Set(pilotFlights.flatMap(f => [f.departure_airport, f.arrival_airport]).filter(Boolean))];
              const defaultActions = [
                { label: 'EBOS', query: 'EBOS weather' },
                { label: 'LEMG', query: 'LEMG weather' },
                { label: 'LFAC', query: 'LFAC weather' }
              ];
              
              if (flightAirports.length > 0) {
                return flightAirports.slice(0, 3).map(apt => ({ label: apt, query: `${apt} weather` }));
              }
              return defaultActions;
            })().map((action, i) => (
              <button
                key={i}
                onClick={() => {
                  setInputValue(action.query);
                  inputRef.current?.focus();
                }}
                className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors border border-slate-700"
              >
                {action.label}
              </button>
            ))}
            {/* My flights weather */}
            {pilotFlights.length > 0 && (
              <button
                onClick={() => {
                  setInputValue('Weather for my flights');
                  inputRef.current?.focus();
                }}
                className="px-3 py-1.5 text-xs bg-indigo-800 hover:bg-indigo-700 text-indigo-100 rounded-lg transition-colors border border-indigo-600"
              >
                My Wx
              </button>
            )}
            {/* Schedule questions */}
            <button
              onClick={() => {
                setInputValue("Who's flying tomorrow?");
                inputRef.current?.focus();
              }}
              className="px-3 py-1.5 text-xs bg-purple-800 hover:bg-purple-700 text-purple-100 rounded-lg transition-colors border border-purple-600"
            >
              Tomorrow?
            </button>
            <button
              onClick={() => {
                setInputValue('Show me the full crew schedule');
                inputRef.current?.focus();
              }}
              className="px-3 py-1.5 text-xs bg-purple-800 hover:bg-purple-700 text-purple-100 rounded-lg transition-colors border border-purple-600"
            >
              Schedule
            </button>
            {/* Save Draft Button - only show if there's a session */}
            {sessionId && messages.length > 2 && (
              <button
                onClick={createDraftReport}
                disabled={isLoading}
                className="px-3 py-1.5 text-xs bg-emerald-800 hover:bg-emerald-700 text-emerald-100 rounded-lg transition-colors border border-emerald-600 flex items-center gap-1"
              >
                <Save className="w-3 h-3" />
                Draft
              </button>
            )}
          </div>
        </div>
      </footer>

      {/* Skye Profile Modal (extracted component) */}
      <SkyeProfileModal 
        isOpen={showSkyeProfile} 
        onClose={() => setShowSkyeProfile(false)} 
      />
    </div>
  );
};

export default WeatherChatPage;
